import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportGoalsChart } from './RivalReportOverview';
import { reportDistribution, reportGoalIntervals, reportNumber, reportRate, reportSum } from '../../utils/rivalReport';

const SCOPES = [
  { key: 'total', label: 'stats.total', suffix: 'Total', played: 'partidosJugados', forKey: 'forGoals', againstKey: 'againstGoals' },
  { key: 'home', label: 'stats.home', suffix: 'Local', played: 'partidosJugadosLocal', forKey: 'homeFor', againstKey: 'homeAgainst' },
  { key: 'away', label: 'stats.away', suffix: 'Visitante', played: 'partidosJugadosVisitante', forKey: 'awayFor', againstKey: 'awayAgainst' },
];

function GoalSourceTable({ title, rows, data, missingValue }) {
  const { t, i18n } = useTranslation('reports');
  const titleId = useId();
  const format = (raw, signed) => {
    const valid = ['number', 'string'].includes(typeof raw) && String(raw).trim() !== '';
    const value = signed && valid && Number.isFinite(Number(raw)) ? Number(raw) : reportNumber(raw);
    return value === null ? missingValue : value.toLocaleString(i18n.language, { maximumFractionDigits: 2 });
  };
  return <section className="rr-panel rr-goal-source" aria-labelledby={titleId}>
    <header><span className="rr-eyebrow">{t('modern.goals.sourceEyebrow')}</span>
      <h3 id={titleId}>{t(`modern.goals.${title}`)}</h3></header>
    <div className="rr-data-table rr-global-response-table"><table aria-label={t(`modern.goals.${title}`)}>
      <thead><tr><th scope="col">{t('modern.metric')}</th>
        {SCOPES.map(scope => <th scope="col" key={scope.key}>{t(scope.label)}</th>)}</tr></thead>
      <tbody>{rows.map(([label, field, signed]) => <tr key={field}>
        <th scope="row">{t(`modern.goals.${label}`)}</th>
        {SCOPES.map(scope => <td key={scope.key}>{format(data?.[`${field}${scope.suffix}`], signed)}</td>)}
      </tr>)}</tbody>
    </table></div>
  </section>;
}

function GoalTypes({ title, values, expected, missingValue }) {
  const { t, i18n } = useTranslation('reports');
  const titleId = useId();
  const { counts, shares } = reportDistribution(values, expected ?? reportSum(...values));
  return <section className="rr-goal-types" aria-labelledby={titleId}>
    <h4 id={titleId}>{t(title)}</h4>
    <dl>{['normal', 'penalty', 'own'].map((key, index) => <div key={key}>
      <dt>{t(`modern.goals.${key}`)}</dt>
      <dd aria-label={t(`modern.goals.${key}`)}>{counts[index]?.toLocaleString(i18n.language) ?? missingValue}
        {shares && <small>{shares[index].toLocaleString(i18n.language, { style: 'percent', maximumFractionDigits: 1 })}</small>}
      </dd>
    </div>)}</dl>
    {!shares && <p className="rr-distribution-note">{t('modern.goals.typesUnavailable')}</p>}
  </section>;
}

export default function ReportGoalStatistics({ estadisticas, clasificacion, missingValue = '—' }) {
  const { t, i18n } = useTranslation('reports');
  const titleId = useId();
  const intervals = reportGoalIntervals(estadisticas, clasificacion);
  const goals = estadisticas?.goles || {};
  const matches = estadisticas?.partidos || {};
  const sameSample = reportNumber(matches.partidosJugados) !== null
    && reportNumber(matches.partidosJugados) === reportNumber(clasificacion?.jugados);
  const forTypes = ['golesTotales', 'golesTotalesPenalti', 'golesTotalesPropia'].map(key => goals[key]);
  const againstTypes = ['golesTotalesRecibidos', 'golesTotalesRecibidosPenalti', 'golesTotalesRecibidosPropia'].map(key => goals[key]);
  const totalFor = sameSample ? reportNumber(clasificacion?.golesFavor) : null;
  const totalAgainst = sameSample ? reportNumber(clasificacion?.golesContra) : null;
  const format = value => value === null ? missingValue : value.toLocaleString(i18n.language, { maximumFractionDigits: 2 });
  return <section className="rr-goals-report" aria-labelledby={titleId}>
    <header className="rr-global-heading"><span className="rr-eyebrow">{t('modern.goals.eyebrow')}</span>
      <h2 id={titleId}>{t('sections.goalsStatsExtended')}</h2><p>{t('modern.goals.subtitle')}</p></header>
    <div className="rr-goal-profiles">
      {SCOPES.map(scope => {
        const played = reportNumber(matches[scope.played]);
        const forGoals = scope.key === 'total'
          ? totalFor ?? reportSum(...forTypes) ?? reportSum(...intervals.map(row => row.forGoals))
          : reportSum(...intervals.map(row => row[scope.forKey]));
        const againstGoals = scope.key === 'total'
          ? totalAgainst ?? reportSum(...againstTypes) ?? reportSum(...intervals.map(row => row.againstGoals))
          : reportSum(...intervals.map(row => row[scope.againstKey]));
        return <section className={`rr-panel rr-goal-profile rr-goal-profile-${scope.key}`} key={scope.key} aria-label={t(scope.label)}>
          <header><h3>{t(scope.label)}</h3><p>{t(played === null ? 'modern.sampleUnknown' : 'modern.sample', { count: played })}</p></header>
          <dl className="rr-goal-scoreline">
            {[['stats.goalsFor', forGoals], ['stats.goalsAgainst', againstGoals]].map(([label, value]) => <div key={label}>
              <dt>{t(label)}</dt><dd aria-label={t(label)}>{format(value)}</dd>
            </div>)}
          </dl>
          <dl className="rr-goal-rates">
            {[['modern.goalsForRate', forGoals], ['modern.goalsAgainstRate', againstGoals]].map(([label, value]) => <div key={label}>
              <dt>{t(label)}</dt><dd aria-label={t(label)}>{format(reportRate(value, played))}</dd>
            </div>)}
          </dl>
          <div className="rr-data-table rr-goal-halves"><table aria-label={t('modern.goals.halves')}>
            <thead><tr><th scope="col">{t('modern.goals.halves')}</th><th scope="col">{t('table.gf')}</th><th scope="col">{t('table.gc')}</th></tr></thead>
            <tbody>{[[0, 3, 'firstHalf'], [3, 6, 'secondHalf']].map(([start, end, label]) => <tr key={label}>
              <th scope="row">{t(`modern.goals.${label}`)}</th>
              <td>{format(reportSum(...intervals.slice(start, end).map(row => row[scope.forKey])))}</td>
              <td>{format(reportSum(...intervals.slice(start, end).map(row => row[scope.againstKey])))}</td>
            </tr>)}</tbody>
          </table></div>
        </section>;
      })}
    </div>
    <ReportGoalsChart statistics={estadisticas} standing={clasificacion} missingValue={missingValue} />
    <div className="rr-goal-venues">
      <ReportGoalsChart statistics={estadisticas} standing={clasificacion} scope="home" missingValue={missingValue} />
      <ReportGoalsChart statistics={estadisticas} standing={clasificacion} scope="away" missingValue={missingValue} />
    </div>
    <section className="rr-panel rr-goal-type-panel" aria-label={t('modern.goals.types')}>
      <header><span className="rr-eyebrow">{t('modern.goals.typeEyebrow')}</span><h3>{t('modern.goals.types')}</h3>
        <p>{t('modern.goals.typesHelp')}</p></header>
      <div className="rr-goal-type-grid">
        <GoalTypes title="stats.goalsFor" values={forTypes} expected={totalFor} missingValue={missingValue} />
        <GoalTypes title="stats.goalsAgainst" values={againstTypes} expected={totalAgainst} missingValue={missingValue} />
      </div>
    </section>
    <div className="rr-goal-frequencies">
      <GoalSourceTable title="scoringFrequency" data={estadisticas?.datos_extra} missingValue={missingValue}
        rows={[['oneGoal', 'partidosMarca1Gol'], ['twoGoals', 'partidosMarca2Goles'], ['threeGoals', 'partidosMarca3OMasGoles']]} />
      <GoalSourceTable title="concedingFrequency" data={estadisticas?.datos_extra} missingValue={missingValue}
        rows={[['oneGoal', 'partidosEncaja1Gol'], ['twoGoals', 'partidosEncaja2Goles'], ['threeGoals', 'partidosEncaja3OMasGoles']]} />
    </div>
    <GoalSourceTable title="sourceBalance" data={estadisticas?.datos_extra} missingValue={missingValue}
      rows={[['difference', 'diferenciaGoles', true], ['scored', 'sumaGolesMetidos'], ['conceded', 'sumaGolesRecibidos']]} />
    <p className="rr-performance-help">{t('modern.goals.sourceHelp')}</p>
  </section>;
}
